import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { feedbackApi, usersApi } from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import {
  MessageSquarePlus,
  Inbox,
  Send,
  Loader2,
  Star,
  TrendingUp,
  AlertCircle,
  MessageCircle,
  CheckCircle,
  Clock,
} from "lucide-react";

const FEEDBACK_TYPES = [
  { value: "recognition", label: "Recognition", icon: Star, color: "text-amber-500" },
  { value: "performance", label: "Performance", icon: TrendingUp, color: "text-blue-500" },
  { value: "improvement", label: "Improvement", icon: AlertCircle, color: "text-orange-500" },
  { value: "general", label: "General", icon: MessageCircle, color: "text-slate-500" },
];

const Feedback = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [allUsers, setAllUsers] = useState([]);
  const [feedback, setFeedback] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    to_user_id: "",
    subject: "",
    content: "",
    feedback_type: "general",
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [usersRes, feedbackRes, unreadRes] = await Promise.all([
        usersApi.getAllForFeedback(),
        feedbackApi.getAll(),
        feedbackApi.getUnreadCount(),
      ]);
      setAllUsers(usersRes.data.filter((u) => u.id !== user.id));
      setFeedback(feedbackRes.data);
      setUnreadCount(unreadRes.data.unread_count);
    } catch (error) {
      toast.error("Failed to load feedback");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      await feedbackApi.create(formData);
      toast.success("Feedback sent successfully");
      setDialogOpen(false);
      setFormData({ to_user_id: "", subject: "", content: "", feedback_type: "general" });
      loadData();
    } catch (error) {
      toast.error("Failed to send feedback");
    } finally {
      setSubmitting(false);
    }
  };

  const handleMarkRead = async (feedbackId) => {
    try {
      await feedbackApi.markRead(feedbackId);
      loadData();
    } catch (error) {
      console.error("Failed to mark as read");
    }
  };

  const receivedFeedback = feedback.filter((f) => f.to_user_id === user.id);
  const sentFeedback = feedback.filter((f) => f.from_user_id === user.id);

  const getFeedbackTypeInfo = (type) => {
    return FEEDBACK_TYPES.find((t) => t.value === type) || FEEDBACK_TYPES[3];
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="loading-spinner">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  const FeedbackCard = ({ item, isReceived }) => {
    const typeInfo = getFeedbackTypeInfo(item.feedback_type);
    const TypeIcon = typeInfo.icon;

    return (
      <Card
        className={`transition-all hover:shadow-md ${
          isReceived && !item.is_read ? "border-l-4 border-l-blue-500 bg-blue-500/10" : ""
        }`}
        onClick={() => isReceived && !item.is_read && handleMarkRead(item.id)}
      >
        <CardContent className="pt-4">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-3 flex-1">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center bg-muted`}>
                <TypeIcon className={typeInfo.color} size={20} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <p className="font-medium text-foreground">{item.subject}</p>
                  <Badge variant="outline" className="text-xs">
                    {typeInfo.label}
                  </Badge>
                  {isReceived && !item.is_read && (
                    <Badge className="bg-blue-500 text-white text-xs">New</Badge>
                  )}
                </div>
                <p className="text-sm text-muted-foreground line-clamp-2 mb-2">{item.content}</p>
                <div className="flex items-center gap-4 text-xs text-muted-foreground">
                  <span>
                    {isReceived ? "From:" : "To:"}{" "}
                    <span className="font-medium text-foreground">
                      {isReceived ? item.from_user_name : item.to_user_name}
                    </span>
                  </span>
                  <span className="flex items-center gap-1">
                    <Clock size={12} />
                    {new Date(item.created_at).toLocaleDateString()}
                  </span>
                  {isReceived && item.is_read && (
                    <span className="flex items-center gap-1 text-emerald-500">
                      <CheckCircle size={12} />
                      Read
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="feedback-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Feedback</h1>
          <p className="text-muted-foreground mt-1">Give and receive feedback from your team</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="gap-2 bg-blue-600 hover:bg-blue-700" data-testid="new-feedback-btn">
              <MessageSquarePlus size={18} />
              Give Feedback
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-lg" data-testid="feedback-dialog">
            <DialogHeader>
              <DialogTitle>Give Feedback</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label>Recipient</Label>
                <Select
                  value={formData.to_user_id}
                  onValueChange={(value) => setFormData({ ...formData, to_user_id: value })}
                >
                  <SelectTrigger data-testid="select-recipient">
                    <SelectValue placeholder="Select person" />
                  </SelectTrigger>
                  <SelectContent>
                    {[...allUsers].sort((a, b) => a.name.localeCompare(b.name)).map((u) => (
                      <SelectItem key={u.id} value={u.id}>
                        {u.name} {u.position && `(${u.position})`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Feedback Type</Label>
                <Select
                  value={formData.feedback_type}
                  onValueChange={(value) => setFormData({ ...formData, feedback_type: value })}
                >
                  <SelectTrigger data-testid="select-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {FEEDBACK_TYPES.map((type) => (
                      <SelectItem key={type.value} value={type.value}>
                        <div className="flex items-center gap-2">
                          <type.icon className={type.color} size={16} />
                          {type.label}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="subject">Subject</Label>
                <Input
                  id="subject"
                  value={formData.subject}
                  onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                  placeholder="Brief subject line"
                  required
                  data-testid="subject-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="content">Message</Label>
                <Textarea
                  id="content"
                  value={formData.content}
                  onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                  placeholder="Your feedback..."
                  rows={4}
                  required
                  data-testid="content-input"
                />
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={submitting || !formData.to_user_id}
                  className="bg-blue-600 hover:bg-blue-700"
                  data-testid="submit-feedback-btn"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    "Send Feedback"
                  )}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-blue-500/20 flex items-center justify-center">
                <Inbox className="text-blue-500" size={24} />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Received</p>
                <p className="text-2xl font-bold text-foreground">{receivedFeedback.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-emerald-500/20 flex items-center justify-center">
                <Send className="text-emerald-500" size={24} />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Sent</p>
                <p className="text-2xl font-bold text-foreground">{sentFeedback.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className={unreadCount > 0 ? "border-blue-500/30 bg-blue-500/10" : ""}>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-amber-500/20 flex items-center justify-center">
                <MessageSquarePlus className="text-amber-500" size={24} />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Unread</p>
                <p className="text-2xl font-bold text-foreground">{unreadCount}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Feedback tabs */}
      <Card>
        <Tabs defaultValue="received" className="w-full">
          <CardHeader>
            <TabsList>
              <TabsTrigger value="received" className="gap-2" data-testid="tab-received">
                <Inbox size={16} />
                Received
                {unreadCount > 0 && (
                  <Badge className="ml-1 bg-blue-500 text-white text-xs">{unreadCount}</Badge>
                )}
              </TabsTrigger>
              <TabsTrigger value="sent" className="gap-2" data-testid="tab-sent">
                <Send size={16} />
                Sent
              </TabsTrigger>
            </TabsList>
          </CardHeader>
          <CardContent>
            <TabsContent value="received" className="mt-0 space-y-4">
              {receivedFeedback.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <Inbox className="mx-auto mb-4 text-muted-foreground/50" size={48} />
                  <p>No feedback received yet</p>
                </div>
              ) : (
                receivedFeedback.map((item) => (
                  <FeedbackCard key={item.id} item={item} isReceived={true} />
                ))
              )}
            </TabsContent>
            <TabsContent value="sent" className="mt-0 space-y-4">
              {sentFeedback.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <Send className="mx-auto mb-4 text-muted-foreground/50" size={48} />
                  <p>No feedback sent yet</p>
                </div>
              ) : (
                sentFeedback.map((item) => (
                  <FeedbackCard key={item.id} item={item} isReceived={false} />
                ))
              )}
            </TabsContent>
          </CardContent>
        </Tabs>
      </Card>
    </div>
  );
};

export default Feedback;
